import { throttle } from '@goodboydigital/astro';
import p2 from 'p2';
import { Sprite } from 'pixi.js';

import { Shared } from '../../App';
import { CullingComponent } from '../components/CullingComponent';
import { Game } from '../Game';
import { PhysicsCollisionGroup, PhysicsUtils } from '../physics';
import { PhysicsComponent } from '../physics/PhysicsComponent';
import { ChunkSpriteData } from '../systems/ChunkSystem';
import { BaseEntity } from './BaseEntity';

export const defaultWreckingBallParams = {
    label: 'WreckingBall',
    x: 0,
    y: 0,
    r: 0,
    sx: 1,
    sy: 1,
    props: {
        speed: 2,
        range: 1.5,
    },
};

export type WreckingBallParams = typeof defaultWreckingBallParams;

export class WreckingBallEntity extends BaseEntity
{
    public base: Sprite;
    public ball: Sprite;
    public eyes: Sprite;
    public params!: WreckingBallParams;
    public physics: PhysicsComponent;
    public culling: CullingComponent;
    public chainLength = 0;
    public angle = 0;

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent);
        this.culling = this.addComponent(CullingComponent);
        this.physics.harmPlayer = true;
        this.physics.physicsFixedUpdateRunner.add(this as any);

        this.base = Sprite.from(Shared.assets.getTexture('wreckingball-base'));
        this.base.anchor.set(0.5);
        this.view.addChild(this.base);

        this.eyes = Sprite.from(Shared.assets.getTexture('wreckingball-base-eyes'));
        this.eyes.anchor.set(0.5);
        this.view.addChild(this.eyes);

        this.ball = Sprite.from(Shared.assets.getTexture('wreckingball-ball'));
        this.ball.anchor.set(0.5, 0.044);
        this.view.addChild(this.ball);
    }

    public static match(data: ChunkSpriteData): boolean
    {
        return data.img === 'wrecking-ball';
    }

    public levelReset(): void
    {
        this.angle = 0;
    }

    public build(params: Partial<WreckingBallParams> = {}): void
    {
        this.physics.clearParts();
        this.params = { ...defaultWreckingBallParams, ...params };
        const absx = Math.abs(this.params.sx);
        // const absy = Math.abs(this.params.sy);

        this.chainLength = 278 * absx;

        const body = PhysicsUtils.createBody({
            x: this.params.x,
            y: this.params.y,
            mass: 0,
        },
        {
            type: p2.Shape.CIRCLE,
            radius: 10,
            group: 0,
            mask: 0,
        });

        const anchorBody = PhysicsUtils.createBody({
            x: this.params.x,
            y: this.params.y - (180 * absx),
            mass: 0,
        },
        {
            type: p2.Shape.CIRCLE,
            radius: 10,
            group: 0,
            mask: 0,
        });

        const ballBody = PhysicsUtils.createBody({
            x: this.params.x,
            y: this.params.y + this.chainLength,
            mass: 100,
            type: p2.Body.KINEMATIC,
        },
        {
            type: p2.Shape.CIRCLE,
            radius: 54 * absx,
            group: PhysicsCollisionGroup.OBJECT,
            mask: PhysicsCollisionGroup.VEHICLE,
        });

        this.physics.addParts(body, anchorBody, ballBody);
        this.physics.rotate(this.params.r);
        this.view.scale.set(absx);

        this.base.y = 0;
        this.ball.y = this.base.y - 180;
        this.eyes.y = this.base.y + 80;
        this.angle = 0;

        this.culling.updateBounds(4);
    }

    protected physicsFixedUpdate(): void
    {
        const anchorBody = this.physics.bodies[1];
        const ballBody = this.physics.bodies[2];
        const game = this.scene as Game;
        const x = anchorBody.position[0];
        const y = anchorBody.position[1];
        const t = game.physics.timer.time;
        const s = this.params.props.speed || 2;
        const r = this.params.props.range || 1.5;

        this.angle = (Math.sin(t * s) * r) + Math.PI;
        ballBody.position[0] = x + (Math.sin(this.angle) * this.chainLength);
        ballBody.position[1] = y + (Math.cos(this.angle) * this.chainLength);

        this.ball.rotation = this.angle + Math.PI - this.params.r;
        this.eyes.x = this.base.x + (Math.sin(-this.ball.rotation) * 5);
        this.eyes.y = this.base.y + 83 + (Math.cos(-this.ball.rotation) * 4);

        if (this.angle > Math.PI - 0.05 && this.angle < Math.PI + 0.05)
        {
            const approxDist = Math.abs(this.view.x);

            if (approxDist < 1500)
            {
                const volume = 1 - (approxDist / 1500);

                throttle(this.playSfx, 100, this)(volume);
            }
        }
    }

    private playSfx(volume: number)
    {
        this.game?.soundSystem.playSfx('windmill', volume);
    }
}

export class WreckingBallFactory
{
    public static match(data: Record<string, any>): boolean
    {
        return data.type === 'SpriteEntity' && data.img === 'wrecking-ball';
    }

    public static create(game: Game, data: Record<string, any>): void
    {
        const entity = game.pool.get(WreckingBallEntity) as WreckingBallEntity;

        entity.build(data as WreckingBallParams);

        game.level.addEntity(entity);
    }
}

